import { Collapse } from '@appd/uik';
import { useCallback, useEffect, useState } from 'react';
import { cloneDeep, get, isEqual, keys, pick, set } from 'lodash';
import { deepMerge } from '../../../../../utils/deepMerge';
import {
  WidgetProps,
  VisualizationTypeOption,
  VisualizationType,
  PropsMap,
  PropertiesPanelContentProps,
} from '../../models';
import { getVisualizationType } from '../../utils';
import { VisualizationStrategy } from '../../visualizationTypes/models';
import { CardOptionsSection, VisualizationTypeSelector } from '.';
import { ExpressionJson } from '../../../../models';

interface WidgetPropertiesPanelContentProps extends PropertiesPanelContentProps {
  updateType: (newType: VisualizationTypeOption) => void;
  expressions: Record<string, ExpressionJson>;
}

export const WidgetPropertiesPanelContent = ({
  widget,
  updateType,
  updateWidget,
  expressions,
}: WidgetPropertiesPanelContentProps): JSX.Element => {
  const [widgetProps, setWidgetProps] = useState<WidgetProps>(widget);
  const [visualizationType, setVisualizationType] = useState<VisualizationTypeOption>(
    getVisualizationType(widget?.component?.name),
  );
  const editableComponent = VisualizationStrategy.getEditableComponent(visualizationType);
  const VisualizationPropertiesPanel = editableComponent.propertiesPanel;

  // store selected properties for each visualization type
  const [editablePropsMap, setEditablePropsMap] = useState<PropsMap>({
    [visualizationType]: widget?.component?.props?.editable ?? {},
  });

  useEffect(() => {
    setVisualizationType(getVisualizationType(widget?.component?.name));
    setWidgetProps(widget);
  }, [widget]);

  const onTypeChange = useCallback(
    (newType: VisualizationTypeOption) => {
      setVisualizationType(newType);

      const newEditableComponent = VisualizationStrategy.getEditableComponent(newType);

      // ignore switching to a visualization type that doesn't have an editor template (VisualizationType.OTHER)
      if (!newEditableComponent.editorTemplate) {
        return;
      }

      // update editable props map
      const updatedEditablePropsMap = cloneDeep(editablePropsMap);
      const newEditableComponentProps =
        get(editablePropsMap, newType) ?? // default to previously used props for selected visualization type
        deepMerge(
          {},
          pick(
            get(editablePropsMap, visualizationType),
            keys(newEditableComponent.editorTemplate.spec.editablePropsDefaults),
          ), // for shared properties, use values from previous visualization type
          newEditableComponent.editorTemplate.spec.editablePropsDefaults,
        ); // use default props for selected visualization type
      set(updatedEditablePropsMap, newType, newEditableComponentProps);
      setEditablePropsMap(updatedEditablePropsMap);

      updateWidgetProps({
        container: widgetProps.container ?? {},
        component: {
          name: newEditableComponent.name,
          props: {
            editable: newEditableComponentProps,
          },
        },
      });

      updateType(newType);
    },
    [editablePropsMap, visualizationType, widgetProps],
  );

  const updateEditableComponentProps = useCallback(
    (newProps: Record<string, any>) => {
      const propsToMerge = {};
      Object.entries(newProps).forEach(([key, value]) => set(propsToMerge, key, value));
      const newEditableComponentProps = deepMerge({}, propsToMerge, widgetProps.component?.props?.editable ?? {});
      setEditablePropsMap(set(editablePropsMap, visualizationType, newEditableComponentProps));
      updateWidgetProps({
        component: {
          props: {
            editable: newEditableComponentProps,
          },
        },
      });
    },
    [editablePropsMap, visualizationType, widgetProps],
  );

  const updateWidgetProps = useCallback(
    (newProps: Record<string, any>) => {
      const updatedWidgetProps = deepMerge({}, newProps, widgetProps);
      if (!isEqual(widgetProps, updatedWidgetProps)) {
        updateWidget(updatedWidgetProps);
        setWidgetProps(updatedWidgetProps);
      }
    },
    [widgetProps],
  );

  return (
    <div className="flex flex-col h-full gap-4">
      <VisualizationTypeSelector
        selected={visualizationType}
        onSelectChange={onTypeChange}
        options={Object.keys(VisualizationStrategy.VISUALIZATION_TYPE_STRATEGY_MAP)
          // Only include "Other" in the dropdown when it is selected
          .filter(type => visualizationType === VisualizationType.OTHER || type !== VisualizationType.OTHER)
          .map(type => new VisualizationStrategy.VISUALIZATION_TYPE_STRATEGY_MAP[type]().getEditableComponent())}
      />
      {widgetProps.container && (
        <CardOptionsSection
          props={widgetProps?.container?.props ?? {}}
          updateProps={(newProps: Record<string, any>) =>
            updateWidgetProps({ container: { name: widgetProps.container?.name, props: newProps } })
          }
        />
      )}
      {/** Only show Visualization Properties Panel for visualization types that support editing */}
      {visualizationType !== VisualizationType.OTHER &&
        VisualizationPropertiesPanel &&
        editableComponent.editorTemplate && (
          <div data-testid="dashify-widget-properties-panel-visualization-options">
            <VisualizationPropertiesPanel
              props={deepMerge(
                {},
                widgetProps?.component?.props?.editable ?? {},
                editableComponent.editorTemplate.spec.editablePropsDefaults, // fallback to defaults from editor template
              )}
              updateProps={(newProps: Record<string, any>) => updateEditableComponentProps(newProps)}
              expressions={keys(expressions)}
            />
          </div>
        )}
    </div>
  );
};
